// The only module that imports maplibre-gl for thumbnails (dynamically, so it stays out of the main chunk and out of
// jest). One hidden map renders every job in turn and the canvas is captured as a JPEG data URL.
import type { Map as MapLibreMap } from 'maplibre-gl';
import { basemapStyle } from '../config/theme';
import type { RendererFactory, ThumbnailJob, ThumbnailRenderer } from './thumbnailQueue';

export const RENDER_TIMEOUT_MS = 8000;
/** Symbol layers whose id starts with this keep their labels (cities, towns); all other labels are hidden. */
const KEPT_LABEL_PREFIX = 'place_';
const JPEG_QUALITY = 0.85;

function once(map: MapLibreMap, event: 'idle' | 'style.load', timeoutMs?: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer =
      timeoutMs === undefined ? undefined : setTimeout(() => reject(new Error(`thumbnail ${event} timed out`)), timeoutMs);
    map.once(event, () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

function hideOtherLabels(map: MapLibreMap): void {
  for (const layer of map.getStyle().layers ?? []) {
    if (layer.type === 'symbol' && !layer.id.startsWith(KEPT_LABEL_PREFIX)) {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
    }
  }
}

export const createMaplibreRenderer: RendererFactory = async () => {
  if (typeof WebGLRenderingContext === 'undefined') throw new Error('WebGL is not available');
  const { Map } = await import('maplibre-gl');

  let container: HTMLDivElement | null = null;
  let map: MapLibreMap | null = null;
  let loadedDark: boolean | undefined;
  let size = { w: 0, h: 0 };
  let abortRunning: ((err: Error) => void) | null = null;

  const dispose = () => {
    map?.remove();
    container?.remove();
    map = null;
    container = null;
    loadedDark = undefined;
  };

  const create = (job: ThumbnailJob): MapLibreMap => {
    container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    container.style.cssText = `position:fixed;left:-10000px;top:0;pointer-events:none;width:${job.width}px;height:${job.height}px`;
    document.body.appendChild(container);
    size = { w: job.width, h: job.height };
    loadedDark = job.dark;
    const created = new Map({
      container,
      style: basemapStyle(job.dark),
      center: job.view.center,
      zoom: job.view.zoom,
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      // Top-level option in maplibre-gl v4; it moves to canvasContextAttributes in v5.
      preserveDrawingBuffer: true,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      trackResize: false,
    });
    created.getCanvas().addEventListener('webglcontextlost', () => {
      abortRunning?.(new Error('WebGL context lost'));
      dispose();
    });
    return created;
  };

  const renderer: ThumbnailRenderer = {
    async render(job) {
      const fresh = !map;
      const m = map ?? (map = create(job));
      const aborted = new Promise<never>((_, reject) => {
        abortRunning = reject;
      });
      const run = async () => {
        if (fresh) {
          await once(m, 'style.load', RENDER_TIMEOUT_MS);
        } else {
          if (size.w !== job.width || size.h !== job.height) {
            container!.style.width = `${job.width}px`;
            container!.style.height = `${job.height}px`;
            size = { w: job.width, h: job.height };
            m.resize();
          }
          if (job.dark !== loadedDark) {
            loadedDark = job.dark;
            m.setStyle(basemapStyle(job.dark));
            await once(m, 'style.load', RENDER_TIMEOUT_MS);
          }
        }
        hideOtherLabels(m);
        m.jumpTo({ center: job.view.center, zoom: job.view.zoom, bearing: 0, pitch: 0 });
        m.triggerRepaint();
        await once(m, 'idle', RENDER_TIMEOUT_MS);
        return m.getCanvas().toDataURL('image/jpeg', JPEG_QUALITY);
      };
      try {
        return await Promise.race([run(), aborted]);
      } catch (err) {
        dispose(); // a half-loaded map must not serve the next job
        throw err;
      } finally {
        abortRunning = null;
      }
    },
    dispose,
  };
  return renderer;
};
